import { Event, FormComponent, MoneyValues, OrderedList, PaymentMethod, ServiceFee, ShopStyle, TimeSlot } from "./TicketApi";

export interface OrderInvoice {
    invoice_number: string;
    url: string;
}

export interface OrderPayout {
    description: string;
    amount: number;
    currency: string;
    processed: boolean;
    status?: RefundStatus | null;
    completedAt?: string | null;
    failedAt?: string | null;
    /** True when this payout returns a refund the provider could not pay back (FAS-2057). */
    failedRefund?: boolean;
}

export type RefundStatus = 'requested' | 'accepted' | 'sent_to_bank' | 'completed' | 'failed';

export interface OrderRefund {
    refundId: string;
    /** Cents. */
    amount: number;
    currency: string;
    status: RefundStatus;
    description: string | null;
    requestedAt: string | null;
    completedAt: string | null;
    failedAt: string | null;
}

export interface PayoutBankDetails {
    accountHolder: string;
    iban: string;
}

export interface OrderWallet {
    walletId: string;
    status: 'pending' | 'available';
    originalBalance?: MoneyValues;
    balance?: MoneyValues;
    qrCode?: string;
    walletUrl?: string;
}

export interface OrderMessage {
    key: string;
    severity: 'info' | 'warning' | 'error';
    description: string;
}

// Blocks the organizer configured for the confirmation email, filtered on the
// ticket types in this order. Text blocks carry sanitized ready-to-render HTML;
// document blocks carry a short-lived signed download url.
export type ConfirmationBlock =
    | { type: 'text'; html: string; url: null; label: null }
    | { type: 'document'; html: null; url: string; label: string };

export interface Order {
    orderId: string;
    shopStyle: ShopStyle;
    tickets: Ticket[];
    wallets?: Record<string, OrderWallet> | OrderWallet[];
    messages: OrderMessage[];
    event: Event;
    formAnswers: Record<string, object[] | string[]>;
    contactDetails: {
        firstName: string;
        lastName: string;
        email: string;
    };
    marketplaceUrl: string|null;
    /** FAS-974 — set when the event runs a visitor app: "open in app" landing page for this order. */
    visitorApp?: { name: string; url: string } | null;
    confirmationBlocks?: ConfirmationBlock[];
    invoices: OrderInvoice[];
    payouts?: OrderPayout[];
    refunds?: OrderRefund[];
    requiresPayoutBankDetails?: boolean;
    /** Why bank details are asked for: a marketplace sale, or a refund that could not be paid back. */
    payoutBankDetailsReason?: 'sale' | 'failed_refund';
    payoutBankDetails?: PayoutBankDetails | null;
}

export interface SharedTicket {
    orderId: string;
    shopStyle: ShopStyle;
    ticket: Ticket;
    event: Event;
}

export interface PendingPersonalisationTicket {
    shopStyle: ShopStyle;
    event: Event;
    paymentMethods: PaymentMethod[];
    price: MoneyValues;
    paidShareFee: ServiceFee|null;
    service_fee: MoneyValues;
    share_code: string;
    form: OrderedList<FormComponent>;
    should_pay: boolean;
    shared_to: string|null;
    shared_to_email: string|null;
    shared_by: string;
    productName: string;
    payment_pending: boolean;
    paid: boolean;
    orderId: string|null;
}

interface BaseTicket {
    ticketId: string;
    productId: string;
    productName: string;
    price: MoneyValues;
    serviceFee: MoneyValues;
    numberOfProductType: number;
    totalOfProductType: number;
    /**
     * FAS-1053 — the entrance time slot this ticket was bought for, frozen at purchase. The
     * ticket is only valid inside it, so it is shown on the card and repeated in the QR modal.
     */
    timeSlot?: TimeSlot|null;
    selfClaimed?: boolean;
    canShare?: boolean;
    ticketMustBePersonalized: boolean;
    requiresPersonalisationUpdate?: boolean;
    personalisationUpdate?: {
        form: OrderedList<FormComponent>;
        answers: Record<string, object[] | string[]>;
    };
    parent?: {
        parentTicketId: string;
        parentTicketHolderName: string;
        parentProductName: string;
    } | null;
    external_link?: {
        href: string;
        title: string;
    };
    sealedSecureSwapUrl?: string | null;
    /**
     * FAS-974 — true when this ticket was claimed from someone else's share, so it
     * lives in a personalisation order. Such a ticket may be handed back but not
     * passed on; the API refuses a second share either way.
     */
    receivedThroughShare?: boolean;
    /** First name of the person who shared it, when this ticket came from a share. */
    sharedByName?: string | null;
    /**
     * Whether this ticket can be handed back to the person who shared it. Only true for
     * a ticket that was given away for free — one the holder paid for is theirs to keep,
     * share on, or resell.
     */
    canReturn?: boolean;
}

export interface ResaleProperties {
    originalPriceInclFee: MoneyValues;
    maxResellPrice: MoneyValues;
    maxResellPercentage: number;
    resaleBasePrice?: MoneyValues | null;
    profitSplitPercentageForOrganizer: number;
    resaleFeeSeller: ServiceFee;
    resaleFeeBuyer: ServiceFee;
    onMarketplace: boolean;
    listingPriceExFee?: MoneyValues;
}

type WithResale<T> = T & {
    canResell: boolean;
} & (
    | { canResell: true; } & ResaleProperties
    | { canResell: false; }
);

// export interface AvailableTicket extends BaseTicket, sharableTicketAttributes {
//     ticketStatus: TicketStatus.available;
//     wallet_download_url: string|null;
//     pdf_download_url: string|null;
//     barcode: string;
// }

export interface sharableTicketAttributes {
    claimForm: OrderedList<FormComponent>;
    allowPaidShare: boolean;
    isSelfClaimable: boolean;
    paidShareFee: ServiceFee|null;
}

// Note: extending BaseTicket here in the inner type
export type ClaimableTicket = WithResale<BaseTicket & {
    ticketStatus: TicketStatus.claimable;
    ticketType: TicketType.EntranceTicket | TicketType.TapConnect;
} & sharableTicketAttributes>;

export enum TicketType {
    EntranceTicket = "EntranceTicket",
    TapConnect = "TapConnect",
    GroupedTicket = "GroupedTicket",
    DonationTicket = "DonationTicket",
    CashCard = "CashCard",
    CashlessWallet = "CashlessWallet",
}

export type AvailableTicket = WithResale<BaseTicket & {
    ticketStatus: TicketStatus.available;
    ticketType: TicketType;
} & (
    | {
        ticketType: TicketType.EntranceTicket;
        sealed: boolean;
        availableFrom: string|null;
        wallet_download_url: string|null;
        pdf_download_url: string|null;
        barcode: string;
        isScanned: boolean;
        firstScannedAt: string|null;
        noMoreValidEntrances: boolean;
    }
    | {
        ticketType: TicketType.TapConnect;
        tapConnectServiceId: string;
        aztecBase64Png: string;
    }
    | {
        ticketType: TicketType.CashCard;
        cashcard: {
            status: 'pending' | 'active';
            message: string;
            balance?: string;
            qrCode?: string;
            url?: string;
        }
    }
    | {
        ticketType: TicketType.CashlessWallet;
        cashlessWallet: {
            status: 'pending' | 'available';
            walletId?: string;
            originalBalance?: MoneyValues;
            balance?: MoneyValues;
            balanceAmount?: MoneyValues;
            qrCode?: string;
            walletUrl?: string;
        }
    }
) & sharableTicketAttributes>;

export type SharedTicketStatus = BaseTicket & {
    ticketStatus: TicketStatus.shared;
    shareCode: string;
    shareName?: string;
    inviteeShouldPay: boolean;
    onMarketplace: boolean;
    canResell: boolean;
};

export type ForSaleTicket = BaseTicket & {
    ticketStatus: TicketStatus.forSale;
    ticketType: TicketType.EntranceTicket | TicketType.TapConnect;
    listingPriceExFee: MoneyValues;
    listingId: string;
    sellerReceivable: MoneyValues;
    organizationReceivableAmount: MoneyValues;
    resaleFeeAmountSeller: MoneyValues;
    sellerFee: ServiceFee;
    maxResellPercentage: number;
    profitSplitPercentageForOrganizer: number;
    canResell: boolean;
    onMarketplace: boolean;
    externalMarketplace?: string | null;
};

export interface ClaimedTicket extends BaseTicket {
    ticketStatus: TicketStatus.claimed;
    claimedAt: string;
    claimedBy: string;
}

export interface SoldTicket extends BaseTicket {
    ticketStatus: TicketStatus.sold;
    externalMarketplace?: string | null;
}

export interface BlockedTicket extends BaseTicket {
    ticketStatus: TicketStatus.blocked;
    ticketType: TicketType;
    blockedReason: string;
    blocks: Record<string, string>;
    canResell: boolean;
    onMarketplace: boolean;
}

export interface DonationTicket extends BaseTicket {
    ticketType: TicketType.DonationTicket;
    ticketStatus: TicketStatus.available;
    // No QR, no personalization, no resale, etc.
}

export interface RefundedTicket extends BaseTicket {
    ticketStatus: TicketStatus.refunded;
    ticketType: TicketType;
}

export interface RevokedTicket extends BaseTicket {
    ticketStatus: TicketStatus.revoked;
    ticketType: TicketType;
}

export type GroupedTicket = WithResale<BaseTicket & {
    ticketStatus: TicketStatus.available | TicketStatus.claimable | TicketStatus.shared | TicketStatus.forSale | TicketStatus.sold | TicketStatus.blocked | TicketStatus.refunded;
    ticketType: TicketType.GroupedTicket;
    tickets: Ticket[];
}>;

export type Ticket = AvailableTicket | ClaimableTicket | ClaimedTicket | SharedTicketStatus | ForSaleTicket | SoldTicket | GroupedTicket | BlockedTicket | DonationTicket | RefundedTicket | RevokedTicket;

export enum TicketStatus {
    available = "available",
    shared = "shared",
    claimed = "claimed",
    claimable = "claimable",
    forSale = "forSale",
    sold = "sold",
    blocked = "blocked",
    refunded = "refunded",
    revoked = "revoked",
}

export class OrderApi {
    constructor(
        private readonly baseUrl: string,
    ) {
    }
    
    public async getOrder(orderId: string, language?: string): Promise<Order> {
        const url = new URL(`${this.baseUrl}/orders/${orderId}`);
        if (language) {
            url.searchParams.set('language', language);
        }
        
        const res = await fetch(url.toString(), {cache: "no-cache"});
        if(res.status === 404) {
            throw new Error("Order not found");
        }
        return await res.json();
    }

    public async getSharedTicket(ticketId: string, language?: string): Promise<SharedTicket> {
        const url = new URL(`${this.baseUrl}/tickets/shared/${ticketId}`);
        if (language) {
            url.searchParams.set('language', language);
        }
        
        const res = await fetch(url.toString(), {cache: "no-cache"});
        return await res.json();
    }

    public async getPendingPersonalisationTicketByCode(shareCode: string, orderId?: string, language?: string): Promise<PendingPersonalisationTicket> {
        const url = new URL(`${this.baseUrl}/pending-personalisation/${shareCode}`);
        if (orderId) {
            url.searchParams.set('order_id', orderId);
        }
        if (language) {
            url.searchParams.set('language', language);
        }
        
        const res = await fetch(url.toString(), {cache: "no-cache", headers: {
            "Accept": "application/json",
        }});

        if(res.status === 404) {
            throw new Error("Ticket not found");
        }
        return await res.json();
    }

    /**
     * A 422 here is one of two different things. `data` is the per-question bag the form renders
     * inline; `reason` (FAS-1188) is a business rejection of the claim itself — the ticket is no
     * longer claimable, or is not in this order — which has no field to attach to and is shown as
     * one sentence instead. Reading only `data` would leave that case with an empty error bag and
     * no visible feedback at all.
     */
    public async selfClaimTicket(orderId: string, ticketId: string, answers: object): Promise<{ success: true, data: unknown } | { success: false, errors: Record<string, string[]>, reason?: string | null }> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/tickets/${ticketId}/self-claim`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                answers: answers
            }),
        });
        if (res.status === 422) {
            const data = await res.json();
            if (typeof data?.reason === "string") {
                return { success: false, errors: {}, reason: data.reason };
            }
            return { success: false, errors: data.data || {} };
        }
        if (!res.ok) {
            return { success: false, errors: {}, reason: null };
        }
        const data = await res.json();
        return { success: true, data };
    }

    /**
     * FAS-974 — hand a claimed ticket back to the person who shared it. `orderId` is
     * the holder's own order; the API resolves the original order from it.
     *
     * On refusal the API sends a stable `reason` code next to its English `message`;
     * the caller translates the code, because this text is shown to a customer. The
     * code is null for a transport-level failure, where there is no body to read.
     */
    public async returnTicket(orderId: string, ticketId: string): Promise<{ success: true } | { success: false; reason: string | null }> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/tickets/${ticketId}/return`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) {
            const body = await res.json().catch(() => null);
            return { success: false, reason: typeof body?.reason === "string" ? body.reason : null };
        }

        return { success: true };
    }

    public async amendTicketAnswers(orderId: string, ticketId: string, answers: object): Promise<{ success: true, data: unknown } | { success: false, errors: Record<string, string[]> }> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/tickets/${ticketId}/amend-answers`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                answers: answers
            }),
        });
        if (res.status === 422) {
            const data = await res.json();
            return { success: false, errors: data.data || {} };
        }
        if (!res.ok) {
            return { success: false, errors: { general: ["Failed to update ticket details"] } };
        }
        const data = await res.json();
        return { success: true, data };
    }

    public async claimTicket(shareCode: string, answers: object, paymentMethod: string|null = null): Promise<{ success: true, data: {type: "no_payment", new_order_id: string} | {type: "redirect", url: string} } | { success: false, errors: Record<string, string[]> }> {
        const res = await fetch(`${this.baseUrl}/pending-personalisation/${shareCode}/claim`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                answers: answers,
                paymentMethodId: paymentMethod,
                redirectAfterPaymentUrl: window.location.href,
            }),
        });
        if (res.status === 422) {
            const data = await res.json();
            return { success: false, errors: data.data || {} };
        }
        if (!res.ok) {
            return { success: false, errors: { general: ["Failed to claim ticket"] } };
        }
        const data = await res.json();
        return { success: true, data };
    }

    public async shareTicket(
        orderId: string,
        ticketId: string,
        name?: string,
        inviteeShouldPay: boolean = false,
        recipientEmail?: string,
        language?: string,
    ): Promise<string> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/tickets/${ticketId}/share`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                name: name,
                inviteeShouldPay: inviteeShouldPay,
                recipientEmail: recipientEmail || null,
                language: language || null,
            }),
        });
        const response = await res.json();
        return response.share_code;
    }

    public async listTicket(orderId: string, ticketId: string, price: MoneyValues): Promise<string> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/tickets/${ticketId}/list`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
            body: JSON.stringify({
                listing_price: price.amount,
            }),
        });
        const response = await res.json();
        return response.listingId;
    }

    public async unlistTicket(orderId: string, ticketId: string): Promise<true|string> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/tickets/${ticketId}/unlist`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
            body: JSON.stringify({
            }),
        });
        const response = await res.json();
        
        if(response.success) {
            return true;
        }

        return response.reason;
    }

    public async cancelShareTicket(orderId: string, ticketId: string): Promise<{success: boolean, message?: string}> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/tickets/${ticketId}/cancel-share`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
        });
        const response = await res.json();
        if (res.status === 400) {
            return { success: false, message: response.message };
        }
        if (res.ok) {
            return { success: true };
        }
        return { success: false, message: response.message || "Something went wrong" };
    }

    public async generateInvoice(
        orderId: string,
        invoiceSequenceId: string,
        data: {
            name: string;
            address_line_1: string;
            address_line_2: string;
            address_line_3?: string;
            business_name?: string;
            purchase_order_number?: string;
        }
    ): Promise<{ success: true; invoice: OrderInvoice } | { success: false; errors: Record<string, string[]> }> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/invoice`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
            body: JSON.stringify({
                ...data,
                invoice_sequence_id: invoiceSequenceId,
            }),
        });
        
        if (res.status === 422) {
            const errorData = await res.json();
            return { success: false, errors: errorData.errors || {} };
        }
        
        if (!res.ok) {
            return { success: false, errors: { general: ["Failed to generate invoice"] } };
        }
        
        const response = await res.json();
        return { success: true, invoice: response };
    }

    public async submitPayoutBankDetails(
        orderId: string,
        data: {
            account_holder_name: string;
            iban: string;
        }
    ): Promise<{ success: true } | { success: false; errors: Record<string, string[]> }> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}/payout-bank-details`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
            body: JSON.stringify(data),
        });

        if (res.status === 422) {
            const errorData = await res.json();
            return { success: false, errors: errorData.errors || {} };
        }

        if (!res.ok) {
            return { success: false, errors: { general: ["Failed to save bank details"] } };
        }

        return { success: true };
    }
}
